import { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Database,
  Clock,
  Edit,
  Info,
  User,
  MoreHorizontal,
  Copy,
} from "lucide-react";
import useSWR from "swr";
import Loader from "@/components/ui/Loader";
import dayjs from "dayjs";
import {
  SpineTemplate,
  isChildSpineTemplate,
} from "../../../types/spine-template";
import { getFullscreenTypeLabel } from "../../../utils/fullscreenType";
import { EditTemplateInfoDialog } from "./components/EditTemplateInfoDialog";
import { getTemplateStateInfo } from "../../../types/templateState";
import { Dot } from "components/Dot";
import { useToast } from "@/components/ui/use-toast";
import { TemplateInitDataInfoDisplay } from "./components/TemplateInitDataInfoDisplay";
import { Link } from "react-router-dom";

// 信息字段子组件，减少重复的 label + value 模式
function InfoField({
  label,
  children,
  colSpan2,
}: {
  label: string;
  children: React.ReactNode;
  colSpan2?: boolean;
}) {
  return (
    <div className={`space-y-1${colSpan2 ? " col-span-2" : ""}`}>
      <span className="text-muted-foreground">{label}</span>
      {typeof children === "string" ? <p>{children}</p> : children}
    </div>
  );
}

export function InfoPanel() {
  const { tid } = useParams<{ tid: string }>();
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();

  const {
    data: template,
    isLoading,
    mutate,
  } = useSWR<SpineTemplate>(tid ? [`/spine-templates/${tid}`] : null);

  const handleCopyInitDataId = useCallback(async () => {
    if (!template?.initDataId) return;
    try {
      await navigator.clipboard.writeText(template.initDataId);
      toast({
        title: "已复制",
        description: `Cover ID: ${template.initDataId}`,
      });
    } catch {
      toast({
        variant: "destructive",
        title: "复制失败",
        description: "无法复制到剪贴板",
      });
    }
  }, [template?.initDataId, toast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader>加载模板信息...</Loader>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-muted-foreground">模板不存在</div>
      </div>
    );
  }

  const stateInfo = getTemplateStateInfo(template.state);
  const isChild = isChildSpineTemplate(template);

  return (
    <>
      <div className="space-y-6">
        {/* 顶部操作栏 */}
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setIsEditing(true)}
            variant="default"
            size="sm"
          >
            <Edit className="h-4 w-4 mr-2" />
            编辑
          </Button>
          {isChild && (
            <Badge variant="outline" className="text-xs">
              子模版
            </Badge>
          )}
        </div>

        {/* 基础信息 */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Info className="h-3.5 w-3.5" />
            基础信息
          </h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoField label="模板 ID">{tid || "-"}</InfoField>
            <InfoField label="名称">{template.name || "-"}</InfoField>
            {isChild && (
              <InfoField label="父模版" colSpan2>
                {template.parentId ? (
                  <Link
                    to={`/admin/spine/editor/${template.parentId}`}
                    className="text-primary underline-offset-4 hover:underline font-mono text-xs"
                  >
                    {template.parentId}
                  </Link>
                ) : (
                  "-"
                )}
              </InfoField>
            )}
            <InfoField label="作者名">{template.publisher || "-"}</InfoField>
            <InfoField label="发布者">
              {template.onlinePublisher || "-"}
            </InfoField>
            <InfoField label="描述" colSpan2>
              <p className="whitespace-pre-wrap">{template.intro || "-"}</p>
            </InfoField>
            <InfoField label="人物数">
              {template.character !== undefined
                ? String(template.character)
                : "-"}
            </InfoField>
            <InfoField label="状态">
              <div className="flex items-center gap-2">
                {stateInfo ? (
                  <>
                    <Dot className={stateInfo.className} />
                    <span>{stateInfo.label}</span>
                  </>
                ) : (
                  <span>-</span>
                )}
              </div>
            </InfoField>
            <InfoField label="模板类型">{template.type || "-"}</InfoField>
            <InfoField label="画幅比例">
              {getFullscreenTypeLabel(template.fullscreenType)}
            </InfoField>
          </div>
        </div>

        <Separator />

        {/* 初始数据 */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <User className="h-3.5 w-3.5" />
            初始数据 Avatar
          </h4>
          <div className="relative group">
            <TemplateInitDataInfoDisplay
              cover={template.cover}
              videoCover={template.videoCover}
              videoCoverPreview={template.videoCoverPreview}
              preview={template.preview}
              videoPreview={template.videoPreview}
              videoPreviewPreview={template.videoPreviewPreview}
            />
            {(template.initDataId || template.cover) && (
              <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="h-8 w-8 bg-background/80 backdrop-blur-sm hover:bg-background"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={handleCopyInitDataId}
                      disabled={!template.initDataId}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                        复制 Cover ID
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* 版本信息 */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Database className="h-3.5 w-3.5" />
            版本信息
          </h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoField label="Spine 版本">
              <div>
                {template.spineVersion ? (
                  <Badge variant="outline">v{template.spineVersion}</Badge>
                ) : (
                  "-"
                )}
              </div>
            </InfoField>
            <InfoField label="Spine 指纹">
              {template.spineFingerprint || "-"}
            </InfoField>
          </div>
        </div>

        <Separator />

        {/* 时间信息 */}
        {(template.createTime ||
          template.updateTime ||
          template.publishTime) && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" />
              时间信息
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {template.createTime && (
                <InfoField label="创建时间">
                  {dayjs(template.createTime).format("YYYY-MM-DD HH:mm:ss")}
                </InfoField>
              )}
              {template.updateTime && (
                <InfoField label="更新时间">
                  {dayjs(template.updateTime).format("YYYY-MM-DD HH:mm:ss")}
                </InfoField>
              )}
              {template.publishTime && (
                <InfoField label="发布时间">
                  {dayjs(template.publishTime).format("YYYY-MM-DD HH:mm:ss")}
                </InfoField>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 编辑弹窗 */}
      <EditTemplateInfoDialog
        open={isEditing}
        onOpenChange={setIsEditing}
        template={template}
        onSaveSuccess={() => mutate()}
      />
    </>
  );
}
